import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./MonthlyExpensesEditor.css";
import { 
  useKeywords, 
  useAddKeyword, 
  useUpdateKeyword, 
  useDeleteKeyword 
} from '../hooks/useApi';
import ConfirmationDialog from './ConfirmationDialog';
import { useToast } from './Toast';
import { RESPONSIBLE_PEOPLE } from '../utils/people';

const fieldStyle = {
  width: '100%',
  padding: '8px',
  fontSize: '14px',
  border: '1px solid #d1d5db',
  borderRadius: '4px',
  boxSizing: 'border-box',
  backgroundColor: 'white',
};

const PersonSelect = ({ value, onChange, disabled, id }) => (
  <select
    id={id}
    value={value || ""}
    onChange={onChange}
    disabled={disabled}
    style={fieldStyle}
  >
    <option value="">No default</option>
    {RESPONSIBLE_PEOPLE.map((person) => (
      <option key={person} value={person}>
        {person}
      </option>
    ))}
  </select>
);

PersonSelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  id: PropTypes.string,
};

const KeywordEditor = ({ onSaveSuccess }) => {
  const [newKeyword, setNewKeyword] = useState({ keyword: "", category: "", person: "" });
  const [editingId, setEditingId] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null });
  const { showToast } = useToast();

  // Mobile detection hook
  useEffect(() => {
    const checkScreenSize = () => {
      // Use a higher breakpoint for better mobile detection
      const screenWidth = window.innerWidth;
      const isMobileScreen = screenWidth < 900;
      
      // Also check for touch support (Chrome DevTools simulation includes this)
      const hasTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      
      // Check for mobile user agent (Chrome DevTools changes this)
      const isMobileUA = /iPhone|iPad|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      const isMobileDevice = isMobileScreen || hasTouch || isMobileUA;
      
      console.log('KeywordEditor - Screen width:', screenWidth, 'Touch support:', hasTouch, 'Mobile UA:', isMobileUA, 'Final isMobile:', isMobileDevice);
      setIsMobile(isMobileDevice);
    };
    
    // Initial check
    checkScreenSize();
    
    // Listen for resize events
    window.addEventListener('resize', checkScreenSize);
    
    // Also listen for orientation change (for mobile devices)
    window.addEventListener('orientationchange', checkScreenSize);
    
    return () => {
      window.removeEventListener('resize', checkScreenSize);
      window.removeEventListener('orientationchange', checkScreenSize);
    };
  }, []);

  // React Query hooks
  const { data: keywordsData = [], isLoading, error } = useKeywords();
  const addKeywordMutation = useAddKeyword();
  const updateKeywordMutation = useUpdateKeyword();
  const deleteKeywordMutation = useDeleteKeyword();

  // Helper function to sort keywords alphabetically by keyword
  const sortKeywords = (keywordArray) => {
    return [...keywordArray].sort((a, b) =>
      a.keyword.toLowerCase().localeCompare(b.keyword.toLowerCase()),
    );
  };

  const handleAdd = async () => {
    if (newKeyword.keyword && newKeyword.category) {
      try {
        await addKeywordMutation.mutateAsync(newKeyword);
        setNewKeyword({ keyword: "", category: "", person: "" });
        
        if (onSaveSuccess) {
          onSaveSuccess();
        }
      } catch (err) {
        showToast("Error adding keyword: " + err.message, 'error');
      }
    }
  };

  const handleDelete = (id) => {
    setDeleteConfirm({ isOpen: true, id });
  };

  const confirmDelete = async () => {
    const id = deleteConfirm.id;
    setDeleteConfirm({ isOpen: false, id: null });
    try {
      await deleteKeywordMutation.mutateAsync(id);
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      showToast('Keyword deleted successfully!', 'success');
    } catch (err) {
      showToast("Error deleting keyword: " + err.message, 'error');
    }
  };

  const handleEdit = (id) => {
    setEditingId(id);
  };

  const handleSaveEdit = async (id, keyword, category, person) => {
    if (!keyword || !category) return;
    
    try {
      await updateKeywordMutation.mutateAsync({ id, keyword, category, person: person || null });
      setEditingId(null);
      
      if (onSaveSuccess) {
        onSaveSuccess();
      }
      showToast('Keyword updated successfully!', 'success');
    } catch (err) {
      showToast("Error updating keyword: " + err.message, 'error');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  if (isLoading && (!Array.isArray(keywordsData) || keywordsData.length === 0)) {
    return <div>Loading keywords...</div>;
  }
  if (error) return <div style={{ color: "red" }}>Error: {error.message}</div>;

  const sortedKeywords = sortKeywords(Array.isArray(keywordsData) ? keywordsData : []);

  return (
    <div className="monthly-expenses-editor">
      <h2>Keyword Editor</h2>
      <p>Keywords categorize transactions during import. A default person is applied when that keyword matches, unless the transaction already has a person you set by hand.</p>
      
      {/* Add New Keyword */}
      <div style={{
        backgroundColor: '#f8fafc',
        padding: '16px',
        borderRadius: '8px',
        marginBottom: '20px',
        border: '1px solid #e2e8f0'
      }}>
        <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600' }}>Add New Keyword</h4>
        <div style={{
          display: 'grid',
          gap: '12px',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              fontWeight: '500' 
            }}>Keyword</label>
            <input
              type="text"
              placeholder="Keyword (e.g., 'amazon', 'starbucks')"
              value={newKeyword.keyword}
              onChange={(e) =>
                setNewKeyword({ ...newKeyword, keyword: e.target.value })
              }
              disabled={addKeywordMutation.isPending}
              style={{
                width: '100%',
                padding: '8px',
                fontSize: '14px',
                border: '1px solid #d1d5db',
                borderRadius: '4px',
                boxSizing: 'border-box'
              }}
            />
          </div>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              fontWeight: '500' 
            }}>Category</label>
            <input
              type="text"
              placeholder="Category (e.g., 'Amazon', 'Food & Drink')"
              value={newKeyword.category}
              onChange={(e) =>
                setNewKeyword({ ...newKeyword, category: e.target.value })
              }
              disabled={addKeywordMutation.isPending}
              style={fieldStyle}
            />
          </div>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontSize: '14px', 
              fontWeight: '500' 
            }}>Default person</label>
            <PersonSelect
              value={newKeyword.person}
              onChange={(e) =>
                setNewKeyword({ ...newKeyword, person: e.target.value })
              }
              disabled={addKeywordMutation.isPending}
            />
          </div>
        </div>
        <button 
          onClick={handleAdd}
          disabled={addKeywordMutation.isPending || !newKeyword.keyword || !newKeyword.category}
          style={{
            width: isMobile ? '100%' : 'auto',
            backgroundColor: '#10b981',
            color: 'white',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '500',
            border: 'none',
            borderRadius: '4px',
            cursor: (addKeywordMutation.isPending || !newKeyword.keyword || !newKeyword.category) ? 'not-allowed' : 'pointer',
            opacity: (addKeywordMutation.isPending || !newKeyword.keyword || !newKeyword.category) ? 0.6 : 1
          }}
        >
          {addKeywordMutation.isPending ? "Adding..." : "Add Keyword"}
        </button>
      </div>

      {/* Keywords Table */}
      <div style={{ marginTop: "20px" }}>
        <p><strong>{sortedKeywords.length}</strong> keywords configured {isMobile ? '(Mobile View)' : '(Desktop View)'}</p>
        <p>Screen width: {typeof window !== 'undefined' ? window.innerWidth : 'unknown'}px | Mobile: {isMobile ? 'YES' : 'NO'}</p>
        
        {/* Mobile Card View */}
        {isMobile && (
          <div>
            {sortedKeywords.map((item) => (
              <div key={item.id} style={{ 
                backgroundColor: '#f9fafb', 
                padding: '16px', 
                marginBottom: '12px', 
                borderRadius: '8px', 
                border: '1px solid #d1d5db' 
              }}>
                {editingId === item.id ? (
                  // Editing mode
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151', 
                        marginBottom: '4px' 
                      }}>Keyword</label>
                      <input
                        style={{ 
                          width: '100%', 
                          padding: '8px', 
                          fontSize: '14px', 
                          border: '1px solid #d1d5db', 
                          borderRadius: '4px',
                          boxSizing: 'border-box'
                        }}
                        type="text"
                        defaultValue={item.keyword}
                        id={`edit-keyword-${item.id}`}
                      />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151', 
                        marginBottom: '4px' 
                      }}>Category</label>
                      <input
                        style={{ 
                          width: '100%', 
                          padding: '8px', 
                          fontSize: '14px', 
                          border: '1px solid #d1d5db', 
                          borderRadius: '4px',
                          boxSizing: 'border-box'
                        }}
                        type="text"
                        defaultValue={item.category}
                        id={`edit-category-${item.id}`}
                      />
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151', 
                        marginBottom: '4px' 
                      }}>Default person</label>
                      <select
                        id={`edit-person-${item.id}`}
                        defaultValue={item.person || ""}
                        style={fieldStyle}
                      >
                        <option value="">No default</option>
                        {RESPONSIBLE_PEOPLE.map((person) => (
                          <option key={person} value={person}>
                            {person}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        style={{ 
                          flex: 1,
                          backgroundColor: '#10b981', 
                          color: 'white', 
                          padding: '8px 16px', 
                          fontSize: '14px', 
                          borderRadius: '4px', 
                          border: 'none', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => {
                          const keyword = document.getElementById(`edit-keyword-${item.id}`).value;
                          const category = document.getElementById(`edit-category-${item.id}`).value;
                          const person = document.getElementById(`edit-person-${item.id}`).value;
                          handleSaveEdit(item.id, keyword, category, person);
                        }}
                        disabled={updateKeywordMutation.isPending}
                      >
                        {updateKeywordMutation.isPending ? "Saving..." : "Save"}
                      </button>
                      <button 
                        style={{ 
                          flex: 1,
                          backgroundColor: '#6b7280', 
                          color: 'white', 
                          padding: '8px 16px', 
                          fontSize: '14px', 
                          borderRadius: '4px', 
                          border: 'none', 
                          cursor: 'pointer' 
                        }}
                        onClick={handleCancelEdit}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View mode
                  <div>
                    <div style={{ marginBottom: '8px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151', 
                        marginBottom: '4px' 
                      }}>Keyword</label>
                      <span style={{ fontSize: '16px', fontWeight: 'bold' }}>{item.keyword}</span>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151', 
                        marginBottom: '4px' 
                      }}>Category</label>
                      <span style={{ fontSize: '16px' }}>{item.category}</span>
                    </div>
                    <div style={{ marginBottom: '12px' }}>
                      <label style={{ 
                        display: 'block', 
                        fontSize: '14px', 
                        fontWeight: '500', 
                        color: '#374151', 
                        marginBottom: '4px' 
                      }}>Default person</label>
                      <span style={{ fontSize: '16px' }}>{item.person || "—"}</span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button 
                        style={{ 
                          flex: 1,
                          backgroundColor: '#3b82f6', 
                          color: 'white', 
                          padding: '8px 16px', 
                          fontSize: '14px', 
                          borderRadius: '4px', 
                          border: 'none', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleEdit(item.id)}
                      >
                        Edit
                      </button>
                      <button 
                        style={{ 
                          flex: 1,
                          backgroundColor: '#ef4444', 
                          color: 'white', 
                          padding: '8px 16px', 
                          fontSize: '14px', 
                          borderRadius: '4px', 
                          border: 'none', 
                          cursor: 'pointer' 
                        }}
                        onClick={() => handleDelete(item.id)}
                        disabled={deleteKeywordMutation.isPending}
                      >
                        {deleteKeywordMutation.isPending ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
        
        {/* Desktop Table View */}
        {!isMobile && (
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr>
                  <th className="px-2 py-2 text-left">Keyword</th>
                  <th className="px-2 py-2 text-left">Category</th>
                  <th className="px-2 py-2 text-left">Person</th>
                  <th className="px-2 py-2 text-left">Actions</th>
                </tr>
              </thead>
            <tbody>
              {sortedKeywords.map((item) => (
                <KeywordRow
                  key={item.id}
                  item={item}
                  isEditing={editingId === item.id}
                  onEdit={handleEdit}
                  onSaveEdit={handleSaveEdit}
                  onCancelEdit={handleCancelEdit}
                  onDelete={handleDelete}
                  isUpdating={updateKeywordMutation.isPending}
                  isDeleting={deleteKeywordMutation.isPending}
                />
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, id: null })}
        onConfirm={confirmDelete}
        title="Delete Keyword"
        message="Are you sure you want to delete this keyword? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />
    </div>
  );
};

// Individual keyword row component for better organization
const KeywordRow = ({ 
  item, 
  isEditing, 
  onEdit, 
  onSaveEdit, 
  onCancelEdit, 
  onDelete,
  isUpdating,
  isDeleting 
}) => {
  const [editValues, setEditValues] = useState({
    keyword: item.keyword,
    category: item.category,
    person: item.person || "",
  });

  useEffect(() => {
    if (isEditing) {
      setEditValues({
        keyword: item.keyword,
        category: item.category,
        person: item.person || "",
      });
    }
  }, [isEditing, item.keyword, item.category, item.person]);

  const handleSave = () => {
    onSaveEdit(item.id, editValues.keyword, editValues.category, editValues.person);
  };

  const handleCancel = () => {
    setEditValues({
      keyword: item.keyword,
      category: item.category,
      person: item.person || "",
    });
    onCancelEdit();
  };

  if (isEditing) {
    return (
      <tr>
        <td>
          <input
            type="text"
            value={editValues.keyword}
            onChange={(e) => setEditValues({ ...editValues, keyword: e.target.value })}
            disabled={isUpdating}
          />
        </td>
        <td>
          <input
            type="text"
            value={editValues.category}
            onChange={(e) => setEditValues({ ...editValues, category: e.target.value })}
            disabled={isUpdating}
          />
        </td>
        <td>
          <PersonSelect
            value={editValues.person}
            onChange={(e) => setEditValues({ ...editValues, person: e.target.value })}
            disabled={isUpdating}
          />
        </td>
        <td>
          <button 
            onClick={handleSave} 
            disabled={isUpdating || !editValues.keyword || !editValues.category}
            style={{ marginRight: "5px" }}
          >
            {isUpdating ? "Saving..." : "Save"}
          </button>
          <button onClick={handleCancel} disabled={isUpdating}>
            Cancel
          </button>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td>{item.keyword}</td>
      <td>{item.category}</td>
      <td>{item.person || "—"}</td>
      <td>
        <button 
          onClick={() => onEdit(item.id)} 
          disabled={isUpdating || isDeleting}
          style={{ marginRight: "5px" }}
        >
          Edit
        </button>
        <button 
          onClick={() => onDelete(item.id)} 
          disabled={isUpdating || isDeleting}
          style={{ 
            backgroundColor: "#dc3545",
            color: "white",
            border: "1px solid #dc3545",
            padding: "4px 8px",
            borderRadius: "4px",
            cursor: isUpdating || isDeleting ? "not-allowed" : "pointer",
            opacity: isUpdating || isDeleting ? 0.6 : 1
          }}
        >
          {isDeleting ? "Deleting..." : "Delete"}
        </button>
      </td>
    </tr>
  );
};

KeywordEditor.propTypes = {
  onSaveSuccess: PropTypes.func,
};

KeywordRow.propTypes = {
  item: PropTypes.object.isRequired,
  isEditing: PropTypes.bool.isRequired,
  onEdit: PropTypes.func.isRequired,
  onSaveEdit: PropTypes.func.isRequired,
  onCancelEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isUpdating: PropTypes.bool.isRequired,
  isDeleting: PropTypes.bool.isRequired,
};

export default KeywordEditor;
